import { useSetToast } from "@app/components/Toast";
import { QuestionModal } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal";
import { type QuestionModalConfirmArg } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/types";
import { questionnaireLocaleAtom } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/ctx";
import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { SupportedLocales } from "@app/shared/utils/locales";
import { Table, usePaginatedSelection } from "@design-system/Table";
import {
    ResponseStatus,
    type SupplierTemplateQuestionFilterInput,
} from "@generated/client/graphql";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, type FC } from "react";
import { useQueryParam } from "use-query-params";
import { editQuestionIdAtom, filterParamArgs, usePagination } from "../../ctx";
import {
    useShouldPollTemplateQuestions,
    useTemplateQuestions,
    useTemplateQuestionsMinimal,
    useUpdateSupplierTemplateQuestion,
} from "../../data";
import { type QuestionFilter } from "../../types";
import { ActionBar } from "./ActionBar";
import { useColumns } from "./columns";
import { EmptyState } from "./EmptyState";
import { useSupplierQuestionsTableTranslations } from "./i18n";
export const QuestionTable: FC<{
    questionnaireId?: string;
    isLoading?: boolean;
    chipFilters: QuestionFilter;
}> = ({ questionnaireId, isLoading, chipFilters }) => {
    const currentLocale = useAtomValue(questionnaireLocaleAtom);
    const userLocale = useCurrentUserLocale(SupportedLocales.EN);
    const shouldPollTemplateQuestions = useShouldPollTemplateQuestions();

    const [editQuestionId, setEditQuestionId] = useAtom(editQuestionIdAtom);

    const [search] = useQueryParam(...filterParamArgs.search);

    const filter = useMemo(() => {
        const filter: SupplierTemplateQuestionFilterInput = {};

        if (search && currentLocale) {
            filter.search = {
                locale: currentLocale,
                value: search,
            };
        }
        if (chipFilters.tagIds && Array.isArray(chipFilters.tagIds)) {
            filter.tagIds = chipFilters.tagIds.filter(Boolean) as string[];
        }
        if (chipFilters.types && Array.isArray(chipFilters.types)) {
            filter.types = chipFilters.types;
        }
        if (
            chipFilters.questionGroupIds &&
            Array.isArray(chipFilters.questionGroupIds)
        ) {
            filter.questionGroupIds =
                chipFilters.questionGroupIds.filter(Boolean);
        }
        return filter;
    }, [search, currentLocale, chipFilters]);

    const { pagination, ...apiPagination } = usePagination();

    const { templateQuestions, isPending } = useTemplateQuestions(
        filter,
        apiPagination,
        {
            pollingInterval: shouldPollTemplateQuestions ? 1000 : undefined,
        },
    );

    const { templateQuestionsMinimal } = useTemplateQuestionsMinimal(
        filter,
        {},
        {
            pollingInterval: shouldPollTemplateQuestions ? 1000 : undefined,
        },
    );

    const { paginatedSelection, resetSelection } = usePaginatedSelection(
        templateQuestions?.list,
    );

    const { updateQuestion, isPending: isUpdateQuestionPending } =
        useUpdateSupplierTemplateQuestion();

    const { setToastSuccess, setToastError } = useSetToast();

    const { t, keys } = useSupplierQuestionsTableTranslations();

    const handleEditModalConfirm = ({
        nameMultiLocale,
        descriptionMultiLocale,
        metadata,
        type,
        isRequired,
    }: QuestionModalConfirmArg) => {
        if (!editQuestionId) return;

        updateQuestion(
            {
                id: editQuestionId,
                input: {
                    nameMultiLocale,
                    descriptionMultiLocale,
                    metadata,
                    type,
                    isRequired,
                },
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.updateSupplierTemplateQuestion?.status ===
                            ResponseStatus.Error
                    ) {
                        setToastError(t(keys.toast_error_update_question));
                        return;
                    }
                    setToastSuccess(t(keys.toast_success_update_question));
                },
            },
        );
    };

    const onEditModalOpenChange = (open: boolean) => {
        if (!open) {
            setEditQuestionId(undefined);
        }
    };

    const columns = useColumns(currentLocale ?? userLocale);

    const hasTemplate = !!questionnaireId;
    const isTableLoading = hasTemplate ? isPending || isLoading : isLoading;

    useEffect(() => {
        pagination.onPaginationChange(({ pageSize }) => ({
            pageIndex: 0,
            pageSize,
        }));
        resetSelection();
    }, [filter]);

    return (
        <>
            <Table
                columns={columns}
                data={templateQuestions?.list}
                loading={isTableLoading}
                EmptyBody={EmptyState}
                pagination={pagination}
                paginatedSelection={paginatedSelection}
                rowCount={templateQuestions?.count}
                variant="grid"
            />
            <ActionBar
                unset={resetSelection}
                paginatedSelectedRows={paginatedSelection.paginatedSelectedRows}
                rowCount={templateQuestions?.count}
                questions={templateQuestionsMinimal?.list}
            />
            {questionnaireId && editQuestionId && (
                <QuestionModal
                    open={!!editQuestionId}
                    setOpen={onEditModalOpenChange}
                    questionnaireId={questionnaireId}
                    questionId={editQuestionId}
                    loading={isUpdateQuestionPending}
                    onConfirm={handleEditModalConfirm}
                    withNa
                    withProof
                />
            )}
        </>
    );
};
